import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FolderOpen, X, Loader2 } from "lucide-react";
import ImageLibraryPicker from "./ImageLibraryPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif"];

/**
 * VariationImageInput — reusable image picker for a single `rental_options[]`
 * variation row, used by BOTH Collection page (AdminOutfits) and Catalogue
 * Edit Mode (CatalogueEditMode).
 *
 * Two ways to set the image, identical to the way product-level images already
 * work in AdminOutfits:
 *   1. Upload from desktop → POSTs to `/api/upload/image` (the existing
 *      product-image endpoint — no new backend route needed).
 *   2. Pick from the media library → reuses the existing
 *      `<ImageLibraryPicker>` component already used elsewhere in admin.
 *
 * Props:
 *   value      — current image URL (string)
 *   onChange   — fn(url) called when the URL changes (upload, pick, or clear)
 *   compact    — when true, uses a thumbnail+button layout for the Catalogue
 *                Edit Mode inline grid; otherwise a full-width row (Collection
 *                edit modal).
 *   testIdPrefix — prefix for data-testid attributes
 */
export default function VariationImageInput({ value, onChange, compact = false, testIdPrefix = "" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [libOpen, setLibOpen] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error("Please select a valid image (JPEG, PNG, WebP, or GIF)");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }
    try {
      setUploading(true);
      const token = localStorage.getItem("adminToken");
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload/image`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      onChange(url);
      toast.success("Variation image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onPicked = (url) => onChange(url);

  // Hidden file input shared by both layouts
  const hiddenFile = (
    <input
      ref={inputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/gif"
      onChange={handleFile}
      style={{ display: "none" }}
      data-testid={`${testIdPrefix}variation-image-file`}
    />
  );

  if (compact) {
    return (
      <div className="flex items-center gap-1 min-w-0">
        {hiddenFile}
        {value ? (
          <div className="relative">
            <img
              src={value}
              alt="variation thumbnail"
              className="w-7 h-7 object-cover border border-[#282828]/10"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute -top-1 -right-1 bg-white border border-[#282828]/20 rounded-full text-[#676664] hover:text-red-500 leading-none w-3.5 h-3.5 flex items-center justify-center"
              title="Remove variation image"
              data-testid={`${testIdPrefix}variation-image-clear`}
            >
              <X className="w-2 h-2" />
            </button>
          </div>
        ) : (
          <div className="w-7 h-7 border border-dashed border-[#282828]/15 flex-shrink-0" />
        )}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="text-[10px] text-[#676664] hover:text-[#9A7B4F] disabled:opacity-50 flex items-center gap-0.5"
          title="Upload variation image from your computer"
          data-testid={`${testIdPrefix}variation-image-upload`}
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
        </button>
        <button
          type="button"
          onClick={() => setLibOpen(true)}
          disabled={uploading}
          className="text-[10px] text-[#676664] hover:text-[#9A7B4F] disabled:opacity-50 flex items-center gap-0.5"
          title="Pick variation image from library"
          data-testid={`${testIdPrefix}variation-image-library`}
        >
          <FolderOpen className="w-3 h-3" />
        </button>
        <ImageLibraryPicker open={libOpen} onClose={() => setLibOpen(false)} onSelect={onPicked} />
      </div>
    );
  }

  // Full-width row (Collection edit modal)
  return (
    <div className="flex items-center gap-2 mt-1">
      {hiddenFile}
      {value && (
        <div className="relative flex-shrink-0">
          <img
            src={value}
            alt="variation thumbnail"
            className="w-10 h-10 object-cover border border-[#282828]/10"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-1 -right-1 bg-white border border-[#282828]/20 rounded-full text-[#676664] hover:text-red-500 leading-none w-4 h-4 flex items-center justify-center"
            title="Remove variation image"
            data-testid={`${testIdPrefix}variation-image-clear`}
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </div>
      )}
      <input
        type="url"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste image URL, upload, or pick from library"
        className="flex-1 border border-[#282828]/10 text-sm px-2 py-1.5"
        data-testid={`${testIdPrefix}variation-image-url`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="text-[11px] text-[#676664] hover:text-[#9A7B4F] disabled:opacity-50 flex items-center gap-1 px-2 py-1 border border-[#282828]/10"
        data-testid={`${testIdPrefix}variation-image-upload`}
      >
        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
        Upload
      </button>
      <button
        type="button"
        onClick={() => setLibOpen(true)}
        disabled={uploading}
        className="text-[11px] text-[#676664] hover:text-[#9A7B4F] disabled:opacity-50 flex items-center gap-1 px-2 py-1 border border-[#282828]/10"
        data-testid={`${testIdPrefix}variation-image-library`}
      >
        <FolderOpen className="w-3 h-3" />
        Library
      </button>
      <ImageLibraryPicker open={libOpen} onClose={() => setLibOpen(false)} onSelect={onPicked} />
    </div>
  );
}
